import {
  Component,
  OnInit,
  OnDestroy,
  Injector,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  HostListener,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormlyFormOptions } from '@ngx-formly/core';
import { Subject, takeUntil } from 'rxjs';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Options } from 'sortablejs';
import { FormBuilderService } from '../../../../core/services/form-builder.service';
import { FormBuilderManipulatorClass } from '../../../../core/utils/form-builder-manipulator-class.utils';
import { FormBuilderV2StateService } from '../../services/form-builder-v2-state.service';
import { FormBuilderV2HistoryService } from '../../services/form-builder-v2-history.service';
import { FbBuilderMode } from '../../models/form-builder-v2.models';
import {
  FbContextMenuComponent,
  IContextMenuItem,
} from '../fb-context-menu/fb-context-menu.component';

@Component({
  selector: 'irembogov-fb-v2-canvas',
  templateUrl: './form-builder-v2-canvas.component.html',
  styleUrls: ['./form-builder-v2-canvas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormBuilderV2CanvasComponent implements OnInit, OnDestroy {
  @ViewChild(FbContextMenuComponent) contextMenu!: FbContextMenuComponent;

  sections: FormlyFieldConfig[] = [];
  selectedFieldKey: string | null = null;
  builderMode: FbBuilderMode = 'builder';
  showOutline = false;

  // Live form preview rendered inside the canvas when builderMode === 'preview'.
  previewForm = new FormGroup({});
  previewModel: Record<string, unknown> = {};
  previewFields: FormlyFieldConfig[] = [];
  previewOptions: FormlyFormOptions = {};
  // Becomes true once the initial load/normalisation churn has settled, after
  // which form emissions are recorded as real, undoable user changes.
  private historyReady = false;

  private destroy$ = new Subject<void>();
  private formBuilderManipulator: FormBuilderManipulatorClass;
  private formBuilderService: FormBuilderService;

  sectionSortableOptions: Options = {
    group: 'sections',
    animation: 200,
    dragClass: 'fb-sortable-drag',
    ghostClass: 'fb-sortable-ghost',
    chosenClass: 'fb-sortable-chosen',
    onEnd: () => this.onSectionReorder(),
  };

  constructor(
    private injector: Injector,
    private stateService: FormBuilderV2StateService,
    private historyService: FormBuilderV2HistoryService,
    private cdr: ChangeDetectorRef
  ) {
    this.formBuilderService = this.injector.get(FormBuilderService);
    this.formBuilderManipulator = new FormBuilderManipulatorClass(
      this.injector
    );
  }

  ngOnInit(): void {
    // Subscribe to form fields changes
    this.formBuilderService.formlyFieldsSubject$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.extractSections(data.fields);
        // Central undo/redo capture: every mutation flows through this subject.
        // Skip recording when applying an undo/redo result, and ignore the
        // initial load churn until the baseline is set (see ngOnInit).
        if (this.historyService.isRestoring) {
          this.historyService.isRestoring = false;
        } else if (this.historyReady) {
          this.historyService.pushState(data.fields);
        }
        this.cdr.markForCheck();
      });

    // Let initial load/normalisation settle, then snapshot a single clean
    // baseline so Undo is disabled until the user actually edits something.
    setTimeout(() => {
      this.historyReady = true;
      this.historyService.resetToBaseline(
        this.formBuilderService.formlyFieldsSubject$.value.fields
      );
    }, 800);

    // Subscribe to field selection
    this.formBuilderService.configureFieldAction$
      .pipe(takeUntil(this.destroy$))
      .subscribe(field => {
        this.selectedFieldKey = field?.key?.toString() || null;
        this.cdr.markForCheck();
      });

    // Subscribe to builder mode
    this.stateService.builderMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(mode => {
        this.builderMode = mode;
        if (mode === 'preview') {
          this.buildPreview();
        }
        this.cdr.markForCheck();
      });
  }

  // Render the actual form (via formly) as an end-user would see it. Deep-clone
  // the fields so formly's internal mutations don't touch the builder's working
  // copy, and reset the form/model so expressions evaluate from scratch.
  private buildPreview(): void {
    const fields = this.formBuilderService.formlyFieldsSubject$.value.fields;
    const clone: FormlyFieldConfig[] = fields?.length
      ? JSON.parse(JSON.stringify(fields))
      : [];
    // Leave props untouched: the field-wrapper header is the only element that
    // renders a label for every field type, so suppressing it (hideHeader) left
    // fields unlabelled. hideBuilderWrapper alone strips the builder chrome —
    // field-wrapper hides its key/gear toolbar on that flag.
    this.previewFields = clone;
    this.previewForm = new FormGroup({});
    this.previewModel = {};
    this.previewOptions = { formState: { hideBuilderWrapper: true } };
  }

  // Close the outline overlay when clicking anywhere outside it (but not on the
  // toolbar, whose Outline button owns the open/close toggle).
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showOutline) return;
    const target = event.target as HTMLElement;
    if (target.closest('.fb-outline') || target.closest('.fb-toolbar')) return;
    this.showOutline = false;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFieldSelected(field: FormlyFieldConfig): void {
    this.formBuilderService.configureFieldAction$.next(field);
  }

  onAddSection(): void {
    const newSection: FormlyFieldConfig = {
      key: `section_${Date.now().toString(36)}`,
      type: 'formly-group',
      props: { label: 'New Section' },
      fieldGroup: [
        {
          key: `block_${Date.now().toString(36)}`,
          type: 'block',
          props: { label: 'New Block' },
          fieldGroup: [],
        },
      ],
    };
    this.formBuilderManipulator.addNewSectionToFields(newSection);
    this.onFormChanged();
  }

  onDeleteSection(sectionIndex: number): void {
    this.sections.splice(sectionIndex, 1);
    this.formBuilderService.refreshFormlyForm(true, true);
    this.onFormChanged();
    this.formBuilderService.requestFormlyFieldsSave();
  }

  onFormChanged(): void {
    // refreshFormlyForm re-emits through formlyFieldsSubject$, which the
    // subscription above records into history — no explicit pushState needed.
    this.formBuilderService.refreshFormlyForm(false, true);
    this.cdr.markForCheck();
  }

  onToggleOutline(): void {
    this.showOutline = !this.showOutline;
    this.cdr.markForCheck();
  }

  onContextMenu(
    event: MouseEvent,
    type: 'section' | 'block' | 'field',
    data: { sectionIndex?: number; field?: FormlyFieldConfig }
  ): void {
    const items: IContextMenuItem[] = [];
    if (type === 'section' && data.sectionIndex !== undefined) {
      items.push(
        {
          label: 'Add Block',
          icon: 'fa-solid fa-plus',
          action: () => {
            // Handled by the section component's own "Add Block" control.
          },
        },
        {
          label: 'Delete Section',
          icon: 'fa-solid fa-trash',
          action: () => this.onDeleteSection(data.sectionIndex!),
          danger: true,
        }
      );
    } else if (type === 'field' && data.field) {
      const field = data.field;
      items.push(
        {
          label: 'Duplicate',
          icon: 'fa-solid fa-copy',
          action: () => this.formBuilderService.duplicateFormField(field),
        },
        {
          label: 'Delete',
          icon: 'fa-solid fa-trash',
          action: () => this.formBuilderService.deleteFormField(field),
          danger: true,
        }
      );
    }
    if (items.length > 0) {
      this.contextMenu.show(event, items);
    }
  }

  onExitPreview(): void {
    this.stateService.setBuilderMode('builder');
  }

  onCanvasClick(): void {
    // Deselect field when clicking empty canvas area
    this.formBuilderService.configureFieldAction$.next(null);
  }

  trackBySectionKey(index: number, section: FormlyFieldConfig): string {
    return section.key?.toString() || section.id || `section-${index}`;
  }

  private extractSections(fields: FormlyFieldConfig[]): void {
    // Form structure: fields[0] is the 'sections' wrapper with fieldGroup containing 'formly-group' sections
    if (fields?.length > 0 && fields[0]?.fieldGroup) {
      this.sections = fields[0].fieldGroup;
      this.restoreParentReferences(this.sections, fields[0]);
    } else {
      this.sections = [];
    }
  }

  private restoreParentReferences(
    nodes: FormlyFieldConfig[],
    parent: FormlyFieldConfig
  ): void {
    for (const node of nodes) {
      Object.defineProperty(node, 'parent', {
        value: parent,
        enumerable: false,
        writable: true,
        configurable: true,
      });
      if (node.fieldGroup?.length) {
        this.restoreParentReferences(node.fieldGroup, node);
      }
      const arrayGroup = (node.fieldArray as FormlyFieldConfig | undefined)
        ?.fieldGroup;
      if (arrayGroup?.length) {
        this.restoreParentReferences(arrayGroup, node);
      }
    }
  }

  private onSectionReorder(): void {
    this.formBuilderService.refreshFormlyForm(false, true);
    this.onFormChanged();
  }
}
